import qz from "qz-tray";
import { http } from "@/configurations/axios";

const QZ_ERROR_MESSAGE =
  "Não foi possível conectar ao QZ Tray. Verifique se o QZ Tray está instalado e aberto neste computador.";
export const QZ_RAW_ENCODING = "CP860";

let securitySetupPromise = null;
let operationQueue = Promise.resolve();
let reconnectPromise = null;

// Serializa a reconexão com listagens e impressões de outras telas. Assim uma
// verificação não fecha o socket enquanto um ticket está sendo enviado.
function withQzConnection(operation) {
  const current = operationQueue.then(operation, operation);
  operationQueue = current.catch(() => {});
  return current;
}

// Configura a assinatura digital das requisicoes do QZ Tray. Sem isso o QZ trata
// cada impressao como "anonymous/untrusted" e nao permite memorizar o "Allow"
// (so o "Block"). O backend fornece o certificado publico e assina cada
// requisicao com a chave privada (SHA-512 + RSA, assinatura em base64).
async function setupSecurity() {
  let certificate;
  try {
    const { data } = await http.get("/restaurante/qz/certificate");
    if (data && data.enabled && data.certificate) {
      certificate = data.certificate;
    }
  } catch (error) {
    const wrapped = new Error(
      "Não foi possível carregar o certificado de impressão do Nuts. Tente novamente."
    );
    wrapped.cause = error;
    throw wrapped;
  }

  if (!certificate) {
    throw new Error(
      "Não foi possível carregar o certificado de impressão do Nuts. Tente novamente."
    );
  }

  qz.security.setSignatureAlgorithm("SHA512");

  qz.security.setCertificatePromise((resolve) => {
    resolve(certificate);
  });

  qz.security.setSignaturePromise((toSign) => {
    return (resolve, reject) => {
      http
        .post("/restaurante/qz/sign", { request: toSign })
        .then((response) => {
          const signature =
            response && response.data && response.data.signature;

          if (signature) {
            resolve(signature);
          } else {
            reject(new Error("Não foi possível validar a comunicação segura com o QZ Tray."));
          }
        })
        .catch((error) => {
          const wrapped = new Error(
            "Não foi possível validar a comunicação segura com o QZ Tray."
          );
          wrapped.cause = error;
          reject(wrapped);
        });
    };
  });
}

async function ensureSecurity() {
  if (!securitySetupPromise) {
    securitySetupPromise = setupSecurity().catch((error) => {
      securitySetupPromise = null;
      throw error;
    });
  }
  await securitySetupPromise;
}

async function connect() {
  await ensureSecurity();

  if (qz.websocket.isActive()) {
    return;
  }

  try {
    await qz.websocket.connect();
  } catch (error) {
    try {
      if (qz.websocket.isActive()) {
        await qz.websocket.disconnect();
      }
    } catch (cleanupError) {
      // A falha original da conexão é mais útil para o usuário.
    }
    const wrapped = new Error(QZ_ERROR_MESSAGE);
    wrapped.cause = error;
    throw wrapped;
  }
}

function normalizePrinters(printers) {
  if (Array.isArray(printers)) {
    return printers.filter(Boolean).sort();
  }
  return printers ? [printers] : [];
}

// A confiança é associada ao WebSocket no QZ. A operação protegida abaixo
// confirma que a nova sessão consegue listar impressoras, mas não prova que
// o certificado foi marcado como confiável no Site Manager.
export function reconectarQz() {
  if (reconnectPromise) {
    return reconnectPromise;
  }
  reconnectPromise = withQzConnection(async () => {
    securitySetupPromise = null;
    await ensureSecurity();
    if (qz.websocket.isActive()) {
      await qz.websocket.disconnect();
    }
    await connect();
    return normalizePrinters(await qz.printers.find());
  }).finally(() => {
    reconnectPromise = null;
  });
  return reconnectPromise;
}

export function finalizarTicketEscPos() {
  return "\r\n\r\n\r\n\r\n\x1D\x56\x00";
}

function normalizeRawText(conteudo, cortar = false) {
  const original = String(conteudo || "").trimEnd();
  const jaTemCorte = original.endsWith("\x1D\x56\x00");
  const texto = (jaTemCorte ? original.slice(0, -3) : original)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n/g, "\r\n")
    .trimEnd();

  return cortar || jaTemCorte
    ? `${texto}${finalizarTicketEscPos()}`
    : `${texto}\r\n\r\n\r\n\r\n`;
}

export function listarImpressorasQz() {
  return withQzConnection(async () => {
    await connect();
    return normalizePrinters(await qz.printers.find());
  });
}

export function imprimirTextoQz(impressora, conteudo, { cortar = false } = {}) {
  if (!impressora) {
    return Promise.reject(new Error("Informe a impressora para imprimir o pedido."));
  }

  return withQzConnection(async () => {
    await connect();
    const config = qz.configs.create(impressora, {
      encoding: QZ_RAW_ENCODING,
    });
    return qz.print(config, [
      {
        type: "raw",
        format: "command",
        flavor: "plain",
        data: normalizeRawText(conteudo, cortar),
      },
    ]);
  });
}

export function imprimirPdfQz(impressora, url) {
  return imprimirPdfsQz(impressora, [url]);
}

export function imprimirPdfsQz(impressora, urls) {
  if (
    !impressora ||
    !Array.isArray(urls) ||
    !urls.length ||
    urls.some((url) => !/^https?:\/\//i.test(String(url || "")))
  ) {
    return Promise.reject(new Error("Comprovante ou impressora inválidos."));
  }

  return withQzConnection(async () => {
    await connect();
    const config = qz.configs.create(impressora);
    return qz.print(
      config,
      urls.map((url) => ({
        type: "pixel",
        format: "pdf",
        flavor: "file",
        data: url,
      }))
    );
  });
}

export function imprimirHtmlQz(impressora, conteudo) {
  if (!impressora || !conteudo) {
    return Promise.reject(new Error("Pedido Delivery ou impressora inválidos."));
  }

  return withQzConnection(async () => {
    await connect();
    const config = qz.configs.create(impressora);
    return qz.print(config, [
      {
        type: "pixel",
        format: "html",
        flavor: "plain",
        data: conteudo,
      },
    ]);
  });
}

export async function imprimirTicketsProducao(tickets = []) {
  const validTickets = tickets.filter(
    (ticket) => ticket && ticket.impressora && ticket.conteudo
  );

  const impressos = [];

  for (const ticket of validTickets) {
    try {
      await imprimirTextoQz(ticket.impressora, ticket.conteudo, { cortar: true });
      impressos.push(
        ...(Array.isArray(ticket.ids) ? ticket.ids : [ticket.id]).filter(Boolean)
      );
    } catch (error) {
      // Uma falha de impressora nao aborta as demais; o ticket que falhou
      // nao entra em "impressos" e permanece pendente para a proxima rodada.
      console.warn("Falha ao imprimir ticket de produção", error);
    }
  }

  return impressos;
}

export function ticketTesteProducao(impressora) {
  return [
    "================================",
    "TESTE DE CODIFICAÇÃO CP860",
    "================================",
    "COZINHA",
    "OBSERVAÇÃO",
    "ENDEREÇO",
    "AÇÃO",
    "AÇÚCAR",
    "CORAÇÃO",
    "CAFÉ",
    `Impressora: ${impressora}`,
    "================================",
  ].join("\n") + finalizarTicketEscPos();
}
